import React, { useState, useEffect } from 'react';
import { Mic, BookOpen, Sparkles, ChevronRight, Menu, X, ShieldCheck, BookHeart, LogIn, List } from 'lucide-react';
import HeroSection from './sections/HeroSection';
import TrustBar from './sections/TrustBar';
import ActionSection from './sections/ActionSection';
import TrustSecuritySection from './sections/TrustSecuritySection';
import TwoModesSection from './sections/TwoModesSection';
import GiftSection from './sections/GiftSection';
import PricingSection from './sections/PricingSection';
import FAQSection from './sections/FAQSection';
import Footer from './sections/Footer';
import AuthModal from '../auth/AuthModal';
import { supabase } from '../../lib/supabase';

const ListItem = ({ icon, text }: { icon: any; text: string }) => (
  <div className="flex items-center gap-3 text-stone-700">
    <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 shrink-0">
      {icon}
    </div>
    <span>{text}</span>
  </div>
);

const FeatureCard = ({ icon, title, description }: { icon: any; title: string; description: string }) => (
  <div className="p-6 rounded-2xl bg-stone-50 border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4 border border-stone-100 text-rose-500">
      {icon}
    </div>
    <h3 className="text-xl font-serif font-semibold text-stone-900 mb-2">{title}</h3>
    <p className="text-stone-600 leading-relaxed text-sm">{description}</p>
  </div>
);

const NewLandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot-password'>('login');
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchUserCount = async () => {
      const { data, error } = await supabase
        .from('public_stats')
        .select('stat_value')
        .eq('stat_name', 'user_count')
        .maybeSingle();

      if (!error && data) {
        setUserCount(Number(data.stat_value));
      }
    };

    fetchUserCount();
  }, []);

  // If the user landed here from a protected deep link (e.g. a reminder
  // email CTA), auto-open the login modal so they don't have to hunt for
  // the sign-in button. ProtectedRoute stored the original URL in
  // sessionStorage; AuthenticatedRedirect will route them there after
  // successful login.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('ethernal_protected_redirect');
      if (pending) {
        setAuthMode('login');
        setAuthModalOpen(true);
      }
    } catch {
      // sessionStorage unavailable — silent fallback to manual sign-in.
    }
  }, []);

  const handleLoginClick = () => {
    setAuthModalOpen(true);
    setAuthMode('login');
  };

  const handleStartRegistration = () => {
    setAuthModalOpen(true);
    setAuthMode('register');
  };

  return (
    <div className="min-h-screen font-sans text-stone-800 bg-[#FAF9F6] antialiased selection:bg-rose-100 selection:text-rose-900">
      {/* Navigation */}
      <nav className={`fixed w-full z-sticky transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <BookHeart size={36} className="text-rose-500" />
            <span className="text-2xl font-bold tracking-tight text-stone-800">Ethernal</span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <a href="#como-funciona" className="text-base font-medium text-stone-600 hover:text-rose-500 transition-colors h-10 flex items-center">Cómo funciona</a>
            <a href="#ejemplos" className="text-base font-medium text-stone-600 hover:text-rose-500 transition-colors h-10 flex items-center">Descubre</a>
            <a href="/calculadora" className="text-base font-medium text-stone-600 hover:text-rose-500 transition-colors h-10 flex items-center">Calculadora de tiempo juntos</a>

            <button
              onClick={handleLoginClick}
              className="text-base font-medium text-stone-700 hover:text-stone-900 px-5 h-10 rounded-full hover:bg-stone-100 transition-colors flex items-center gap-2"
            >
              <LogIn size={18} />
              Iniciar sesión
            </button>

            <button
              onClick={handleStartRegistration}
              className="text-base font-medium bg-stone-900 text-white px-6 h-10 rounded-full hover:bg-stone-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center"
            >
              Haz la primera pregunta
            </button>
          </div>

          <div className="md:hidden">
            <button className="p-2 text-stone-800" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-t border-stone-100 p-6 flex flex-col gap-4 shadow-xl animate-fade-in">
            <a href="#como-funciona" onClick={() => setIsMenuOpen(false)} className="text-lg text-stone-600 hover:text-rose-500 transition-colors">Cómo funciona</a>
            <a href="#ejemplos" onClick={() => setIsMenuOpen(false)} className="text-lg text-stone-600 hover:text-rose-500 transition-colors">Descubre</a>
            <a href="/calculadora" onClick={() => setIsMenuOpen(false)} className="text-lg text-stone-600 hover:text-rose-500 transition-colors">Calculadora de tiempo juntos</a>

            <button
              onClick={() => {
                setIsMenuOpen(false);
                handleLoginClick();
              }}
              className="text-stone-700 py-3 rounded-xl w-full font-medium hover:bg-stone-50 transition border border-stone-200"
            >
              Iniciar sesión
            </button>

            <button
              onClick={() => {
                handleStartRegistration();
                setIsMenuOpen(false);
              }}
              className="bg-rose-500 text-white py-3 rounded-xl w-full font-medium mt-2 hover:bg-rose-600 transition shadow-md"
            >
              Haz la primera pregunta
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <HeroSection onStartClick={handleStartRegistration} onLoginClick={handleLoginClick} userCount={userCount} />

      {/* Trust Bar */}
      <TrustBar />

      {/* How It Works */}
      <section id="como-funciona" className="py-8 md:py-20 bg-white scroll-mt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-6 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-stone-900 mb-6">¿Cómo funciona?</h2>
            <p className="text-stone-600 text-lg">Tres pasos. Sin complicaciones.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Mic size={32} />}
              title="1. Pregunta"
              description="Ethernal os guía con preguntas que sacan historias que no esperabais. Solo hay que darle a grabar y hablar."
            />
            <FeatureCard
              icon={<Sparkles size={32} />}
              title="2. Descubre"
              description="Mientras habláis, van saliendo cosas que no sabías. Todo queda transcrito y guardado tal como se contó."
            />
            <FeatureCard
              icon={<BookOpen size={32} />}
              title="3. Guárdalo para siempre"
              description="Su voz, sus fotos, su forma de hablar quedan guardadas en vuestra cápsula del tiempo digital, o en formato libro."
            />
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <ActionSection onRecordClick={handleStartRegistration} />

      {/* Emotive Demo Section */}
      <section id="ejemplos" className="py-8 md:py-24 bg-[#F5F2F0] relative overflow-hidden scroll-mt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16 lg:gap-24">
            <div className="md:w-1/2 relative group w-full px-4 md:px-0">
              <div className="absolute inset-0 bg-stone-900/5 transform rotate-3 rounded-2xl group-hover:rotate-1 transition-transform duration-500"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white rotate-2 group-hover:rotate-0 transition-all duration-500">
                <div className="bg-stone-800 h-[450px] w-full flex items-center justify-center relative">
                  <img
                    src="/unnamed_(4).jpg"
                    alt="Madre e hija riendo"
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-1000"
                  />
                </div>
              </div>
            </div>

            <div className="md:w-1/2 pt-6 md:pt-0">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 mb-8 leading-tight">
                Hay cosas de tus padres que solo descubrirás <span className="text-rose-500">si les preguntas.</span>
              </h2>
              <p className="text-lg text-stone-600 mb-10 leading-relaxed font-light">
                El primer trabajo de tu padre. Cómo se conocieron tus abuelos. Aquella vez que tu madre casi se fue a vivir a otro país. Son historias que están ahí, esperando a que alguien pregunte. Ethernal os da las preguntas y el momento. Todo lo que descubráis queda guardado: su voz, sus fotos, su forma de hablar.
              </p>

              <ul className="space-y-5 mb-12">
                <ListItem icon={<ShieldCheck size={20} />} text="Privado por defecto. Solo tu familia tiene acceso." />
                <ListItem icon={<List size={20} />} text="42 temas en 8 capítulos — desde la infancia hasta los consejos de vida." />
              </ul>

              <button
                onClick={handleStartRegistration}
                className="bg-stone-900 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-stone-800 transition shadow-xl inline-flex items-center gap-2 md:hidden"
              >
                Haz la primera pregunta
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes */}
      <TwoModesSection />

      {/* Trust & Security */}
      <TrustSecuritySection />

      {/* Gift Section */}
      <GiftSection onStartClick={handleStartRegistration} />

      {/* Pricing */}
      <PricingSection onStartClick={handleStartRegistration} />

      {/* FAQ */}
      <FAQSection />

      {/* Footer CTA */}
      <section id="footer-cta" className="py-10 md:py-24 bg-stone-900 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-rose-500/10 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl relative z-10">
          <h2 className="text-3xl md:text-5xl font-serif font-semibold mb-6">La primera pregunta lo cambia todo.</h2>
          <p className="text-stone-400 text-xl mb-12 max-w-2xl mx-auto font-light">
            No necesitas tarjeta de crédito ni equipo especial. Solo tú, tu familiar y las preguntas adecuadas.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleStartRegistration}
              className="bg-rose-500 text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-rose-600 transition shadow-lg hover:shadow-rose-500/30 transform hover:-translate-y-1"
            >
              Empezar
            </button>
          </div>
          {userCount !== null && (
            <p className="mt-10 text-sm text-stone-500">{userCount.toLocaleString()} familias ya han empezado.</p>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />
    </div>
  );
};

export default NewLandingPage;
